import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, FilterBar, FilterChips, IconButton, Pagination, Pill, fmtDataISO, type Column } from "@atlas/ui/web";
import { atlas } from "../../../../lib/sdk";
import type { BancoTabela } from "@atlas/sdk";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../../../lib/recorte";
import { useDebouncedValue } from "../../../../lib/use-debounce";

const fmtPct = (n: number) => `${(n * 100).toFixed(2)}%`;
// fmtDate local removido — usava new Date(iso).toLocaleDateString que
// interpreta "YYYY-MM-DD" como UTC (off-by-one em BRT). fmtDataISO faz slice
// direto do regex, sem instanciar Date. Cliente 28/07/2026.
const fmtDate = (iso?: string) => fmtDataISO(iso);

export function BancoTabelaEmprestimosLista() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [situacao, setSituacao] = useState<"" | "ativo" | "inativo">("");
  const [search, setSearch] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);

  // O input guarda o valor CRU e a consulta usa o atrasado: sem isto cada tecla
  // e uma queryKey nova, ou seja um request.
  const buscaDebounced = useDebouncedValue(search);

  // O escopo por convenio ATIVO agora e do BACKEND (`WHERE convenio_id`), nao
  // mais um `.filter()` sobre a lista inteira. Filtrar aqui recortaria so a
  // pagina corrente: o operador veria apenas as tabelas do convenio que
  // CALHARAM de estar nas 25 linhas visiveis.
  //
  // queryKey ["banco","convenios"] e' o mesmo do layout — trocar o convenio la
  // invalida ["banco"] e refaz esta consulta automaticamente.
  const convQ = useQuery({ queryKey: ["banco", "convenios"], queryFn: () => atlas.banco.convenios() });
  const data = useQuery({
    queryKey: ["banco", "tabelas", situacao, buscaDebounced, pagina, porPagina],
    queryFn: () => atlas.banco.listTabelas({
      situacao: situacao || undefined,
      q: buscaDebounced || undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    placeholderData: (anterior) => anterior,
  });

  /** Trocar filtro com a posicao antiga cairia num offset que o novo recorte
   *  pode nem ter. (O `Pagination` ja volta pra 1 ao trocar o TAMANHO.) */
  const filtrar = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPagina(1); };

  const daPagina = recorteDoServidor(
    data.data?.tabelas ?? [],
    data.data?.total ?? 0,
    pagina,
    porPagina,
  );
  const remove = useMutation({
    mutationFn: (id: string) => atlas.banco.removerTabela(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["banco", "tabelas"] });
      qc.invalidateQueries({ queryKey: ["banco", "tabela", id] });
      qc.invalidateQueries({ queryKey: ["servidor", "ofertas"] });
    },
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => atlas.banco.reativarTabela(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["banco", "tabelas"] });
      qc.invalidateQueries({ queryKey: ["banco", "tabela", id] });
      qc.invalidateQueries({ queryKey: ["servidor", "ofertas"] });
    },
  });

  const columns: Column<BancoTabela>[] = [
    {
      key: "ativo",
      header: "Situação",
      render: (t) => <Pill variant={t.ativo ? "averbado" : "expirado"}>{t.ativo ? "Ativo" : "Inativo"}</Pill>,
    },
    { key: "criadoEm", header: "Data Cadastro", render: (t) => fmtDate(t.criadoEm) },
    { key: "vigenciaInicio", header: "Início Vigência", render: (t) => fmtDate(t.vigenciaInicio) },
    // "Aberta" em vez de "—" quando o banco cadastrou tabela sem fim (convenio
    // sem vigencia fim). "—" ficava ambiguo: parecia bug ("nao esta sendo
    // preenchida").
    { key: "vigenciaFim", header: "Fim Vigência", render: (t) => t.vigenciaFim ? fmtDate(t.vigenciaFim) : "Aberta" },
    { key: "convenio", header: "Convênio" },
    // Nome + faixa: com varias tabelas por convenio, "CET a.m." sozinho nao
    // identifica linha nenhuma. Tabela legada nao tem nome — cai no id, que e'
    // o que a tela mostrava antes. Cliente 20/08/2026.
    { key: "nome", header: "Tabela", render: (t) => t.nome?.trim() || t.id },
    { key: "taxa", header: "CET a.m.", render: (t) => fmtPct(t.taxaAm ?? t.taxaMaxAm ?? 0) },
    {
      key: "prazo",
      header: "Prazo",
      render: (t) => `${t.prazoMinMeses ?? 1}x a ${t.prazoMaxMeses}x`,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Cadastros
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Tabela de Empréstimos</h1>
        </div>
        <Button onClick={() => nav("novo")}>+ Inserir</Button>
      </header>

      <FilterBar
        searchValue={search}
        onSearchChange={filtrar(setSearch)}
        onReset={() => { setSearch(""); setSituacao(""); setPagina(1); }}
      >
        {/* Sem contagem nos chips, pelo mesmo motivo de T045/T058b: o filtro vai
            para a API, entao o numero teria que sair da base inteira — que esta
            tela nao tem mais em memoria — e contagem honesta exigiria a
            semantica do `exceto` no servidor, que nao existe em rota nenhuma
            deste repo. Chip com numero errado seria pior que chip sem numero. */}
        <FilterChips
          label="Situação"
          value={situacao}
          onChange={(v) => filtrar(setSituacao)(v as "" | "ativo" | "inativo")}
          options={[
            { value: "", label: "Todas" },
            { value: "ativo", label: "Ativas" },
            { value: "inativo", label: "Inativas" },
          ]}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(t) => t.id}
        loading={data.isLoading || convQ.isLoading}
        emptyState={
          daPagina.total === 0 && (search || situacao)
            ? "Nenhuma tabela bate com os filtros atuais."
            : "Nenhuma tabela cadastrada. Clique em '+ Inserir' pra publicar suas taxas."
        }
        actions={(t) => (
          <>
            <IconButton title="Editar" onClick={() => nav(t.id)}>✎</IconButton>
            {t.ativo ? (
              <IconButton
                title="Desativar"
                danger
                onClick={() => {
                  if (confirm(`Desativar tabela ${t.id}?\n\nEla para de aparecer nas simulações, mas nada é apagado — você pode reativar depois.`)) remove.mutate(t.id);
                }}
              >
                ⏸
              </IconButton>
            ) : (
              <IconButton title="Reativar" onClick={() => reactivate.mutate(t.id)}>▶</IconButton>
            )}
          </>
        )}
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="tabelas"
      />
    </div>
  );
}
